<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('hashTool.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('hashTool.title') }}</h1>
      <p>{{ $t('hashTool.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="form-group">
        <label>{{ $t('hashTool.inputLabel') }}</label>
        <textarea
          v-model="input"
          :placeholder="$t('hashTool.inputPlaceholder')"
          rows="5"
          spellcheck="false"
          @input="computeAll"
        ></textarea>
      </div>

      <div class="form-group">
        <label>{{ $t('hashTool.outputCase') }}</label>
        <div class="toggle-group">
          <button :class="['toggle-btn', { active: uppercase }]" @click="uppercase = true; computeAll()">{{ $t('hashTool.upper') }}</button>
          <button :class="['toggle-btn', { active: !uppercase }]" @click="uppercase = false; computeAll()">{{ $t('hashTool.lower') }}</button>
        </div>
      </div>

      <div class="hash-results" v-if="results.length">
        <div class="hash-item" v-for="item in results" :key="item.algo">
          <div class="hash-label">{{ item.algo }}</div>
          <div class="hash-value">
            <code>{{ item.hash }}</code>
            <button class="btn-copy" @click="copyHash(item.hash)" :title="$t('common.copy')">📋</button>
          </div>
        </div>
      </div>

      <div class="action-bar" v-if="statusMsg">
        <span class="status-msg">{{ statusMsg }}</span>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('hashTool.whatIs') }}</h2>
      <p>{{ $t('hashTool.whatIsDesc') }}</p>
      <h3>{{ $t('hashTool.algosTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('hashTool.algos')" :key="i">{{ item }}</li>
      </ul>
      <h3>{{ $t('hashTool.useCasesTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('hashTool.useCases')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HashTool',
  data() {
    return {
      input: '',
      uppercase: false,
      results: [],
      statusMsg: ''
    }
  },
  methods: {
    async computeAll() {
      if (!this.input) { this.results = []; return }
      const algos = [
        { name: 'MD5', algo: null },
        { name: 'SHA-1', algo: 'SHA-1' },
        { name: 'SHA-256', algo: 'SHA-256' },
        { name: 'SHA-384', algo: 'SHA-384' },
        { name: 'SHA-512', algo: 'SHA-512' }
      ]
      const encoder = new TextEncoder()
      const data = encoder.encode(this.input)
      const results = []

      // MD5 使用纯 JS 实现
      results.push({ algo: 'MD5', hash: this.formatHash(this.md5(this.input)) })

      // SHA 系列使用 Web Crypto API
      for (const a of algos.slice(1)) {
        try {
          const hashBuffer = await crypto.subtle.digest(a.algo, data)
          const hashArray = Array.from(new Uint8Array(hashBuffer))
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
          results.push({ algo: a.name, hash: this.formatHash(hashHex) })
        } catch {
          results.push({ algo: a.name, hash: 'Not supported' })
        }
      }
      this.results = results
    },
    formatHash(hex) {
      return this.uppercase ? hex.toUpperCase() : hex.toLowerCase()
    },
    async copyHash(hash) {
      try {
        await navigator.clipboard.writeText(hash)
        this.statusMsg = this.$t('common.copied')
        setTimeout(() => { this.statusMsg = '' }, 2000)
      } catch {
        this.statusMsg = this.$t('common.copyFailed')
      }
    },
    // 纯 JS MD5 实现
    md5(string) {
      function md5cycle(x, k) {
        var a = x[0], b = x[1], c = x[2], d = x[3]
        a = ff(a, b, c, d, k[0], 7, -680876936); d = ff(d, a, b, c, k[1], 12, -389564586)
        c = ff(c, d, a, b, k[2], 17, 606105819); b = ff(b, c, d, a, k[3], 22, -1044525330)
        a = ff(a, b, c, d, k[4], 7, -176418897); d = ff(d, a, b, c, k[5], 12, 1200080426)
        c = ff(c, d, a, b, k[6], 17, -1473231341); b = ff(b, c, d, a, k[7], 22, -45705983)
        a = ff(a, b, c, d, k[8], 7, 1770035416); d = ff(d, a, b, c, k[9], 12, -1958414417)
        c = ff(c, d, a, b, k[10], 17, -42063); b = ff(b, c, d, a, k[11], 22, -1990404162)
        a = ff(a, b, c, d, k[12], 7, 1804603682); d = ff(d, a, b, c, k[13], 12, -40341101)
        c = ff(c, d, a, b, k[14], 17, -1502002290); b = ff(b, c, d, a, k[15], 22, 1236535329)
        a = gg(a, b, c, d, k[1], 5, -165796510); d = gg(d, a, b, c, k[6], 9, -1069501632)
        c = gg(c, d, a, b, k[11], 14, 643717713); b = gg(b, c, d, a, k[0], 20, -373897302)
        a = gg(a, b, c, d, k[5], 5, -701558691); d = gg(d, a, b, c, k[10], 9, 38016083)
        c = gg(c, d, a, b, k[15], 14, -660478335); b = gg(b, c, d, a, k[4], 20, -405537848)
        a = gg(a, b, c, d, k[9], 5, 568446438); d = gg(d, a, b, c, k[14], 9, -1019803690)
        c = gg(c, d, a, b, k[3], 14, -187363961); b = gg(b, c, d, a, k[8], 20, 1163531501)
        a = gg(a, b, c, d, k[13], 5, -1444681467); d = gg(d, a, b, c, k[2], 9, -51403784)
        c = gg(c, d, a, b, k[7], 14, 1735328473); b = gg(b, c, d, a, k[12], 20, -1926607734)
        a = hh(a, b, c, d, k[5], 4, -378558); d = hh(d, a, b, c, k[8], 11, -2022574463)
        c = hh(c, d, a, b, k[11], 16, 1839030562); b = hh(b, c, d, a, k[14], 23, -35309556)
        a = hh(a, b, c, d, k[1], 4, -1530992060); d = hh(d, a, b, c, k[4], 11, 1272893353)
        c = hh(c, d, a, b, k[7], 16, -155497632); b = hh(b, c, d, a, k[10], 23, -1094730640)
        a = hh(a, b, c, d, k[13], 4, 681279174); d = hh(d, a, b, c, k[0], 11, -358537222)
        c = hh(c, d, a, b, k[3], 16, -722521979); b = hh(b, c, d, a, k[6], 23, 76029189)
        a = hh(a, b, c, d, k[9], 4, -640364487); d = hh(d, a, b, c, k[12], 11, -421815835)
        c = hh(c, d, a, b, k[15], 16, 530742520); b = hh(b, c, d, a, k[2], 23, -995338651)
        a = ii(a, b, c, d, k[0], 6, -198630844); d = ii(d, a, b, c, k[7], 10, 1126891415)
        c = ii(c, d, a, b, k[14], 15, -1416354905); b = ii(b, c, d, a, k[5], 21, -57434055)
        a = ii(a, b, c, d, k[12], 6, 1700485571); d = ii(d, a, b, c, k[3], 10, -1894986606)
        c = ii(c, d, a, b, k[10], 15, -1051523); b = ii(b, c, d, a, k[1], 21, -2054922799)
        a = ii(a, b, c, d, k[8], 6, 1873313359); d = ii(d, a, b, c, k[15], 10, -30611744)
        c = ii(c, d, a, b, k[6], 15, -1560198380); b = ii(b, c, d, a, k[13], 21, 1309151649)
        a = ii(a, b, c, d, k[4], 6, -145523070); d = ii(d, a, b, c, k[11], 10, -1120210379)
        c = ii(c, d, a, b, k[2], 15, 718787259); b = ii(b, c, d, a, k[9], 21, -343485551)
        x[0] = add32(a, x[0]); x[1] = add32(b, x[1]); x[2] = add32(c, x[2]); x[3] = add32(d, x[3])
      }
      function cmn(q, a, b, x, s, t) { a = add32(add32(a, q), add32(x, t)); return add32((a << s) | (a >>> (32 - s)), b) }
      function ff(a, b, c, d, x, s, t) { return cmn((b & c) | ((~b) & d), a, b, x, s, t) }
      function gg(a, b, c, d, x, s, t) { return cmn((b & d) | (c & (~d)), a, b, x, s, t) }
      function hh(a, b, c, d, x, s, t) { return cmn(b ^ c ^ d, a, b, x, s, t) }
      function ii(a, b, c, d, x, s, t) { return cmn(c ^ (b | (~d)), a, b, x, s, t) }
      function md51(s) {
        var n = s.length, state = [1732584193, -271733879, -1732584194, 271733878], i
        for (i = 64; i <= n; i += 64) { md5cycle(state, md5blk(s.substring(i - 64, i))) }
        s = s.substring(i - 64)
        var tail = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0]
        for (i = 0; i < s.length; i++) { tail[i >> 2] |= s.charCodeAt(i) << ((i % 4) << 3) }
        tail[i >> 2] |= 0x80 << ((i % 4) << 3)
        if (i > 55) { md5cycle(state, tail); tail = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0] }
        tail[14] = n * 8
        md5cycle(state, tail)
        return state
      }
      function md5blk(s) {
        var md5blks = [], i
        for (i = 0; i < 64; i += 4) { md5blks[i >> 2] = s.charCodeAt(i) + (s.charCodeAt(i+1) << 8) + (s.charCodeAt(i+2) << 16) + (s.charCodeAt(i+3) << 24) }
        return md5blks
      }
      var hex_chr = '0123456789abcdef'.split('')
      function rhex(n) {
        var s = '', j = 0
        for (; j < 4; j++) { s += hex_chr[(n >> (j * 8 + 4)) & 0x0F] + hex_chr[(n >> (j * 8)) & 0x0F] }
        return s
      }
      function hex(x) { for (var i = 0; i < x.length; i++) { x[i] = rhex(x[i]) } return x.join('') }
      function add32(a, b) { return (a + b) & 0xFFFFFFFF }

      // 处理 UTF-8
      var utf8 = unescape(encodeURIComponent(string))
      return hex(md51(utf8))
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 900px; margin: 0 auto; }
.breadcrumb { margin-bottom: 16px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb .separator { margin: 0 8px; }
.tool-header { margin-bottom: 24px; }
.tool-header h1 { font-size: 28px; margin-bottom: 8px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 15px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }
.info-card h2 { font-size: 20px; margin-bottom: 12px; color: var(--text-primary); }
.info-card h3 { font-size: 16px; margin: 16px 0 8px; color: var(--text-primary); }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 12px; }
.info-card ul { padding-left: 20px; margin-bottom: 12px; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 4px; }

.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }
.form-group textarea { width: 100%; padding: 12px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-input); color: var(--text-primary); font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 14px; resize: vertical; outline: none; box-sizing: border-box; }
.form-group textarea:focus { border-color: var(--accent-color); }

.toggle-group { display: flex; gap: 8px; }
.toggle-btn { padding: 6px 16px; border: 1px solid var(--border-color); border-radius: 6px; background: var(--bg-input); color: var(--text-secondary); cursor: pointer; font-size: 13px; transition: all 0.2s; }
.toggle-btn.active { background: var(--accent-color); color: white; border-color: var(--accent-color); }

.hash-results { display: flex; flex-direction: column; gap: 12px; }
.hash-item { display: flex; align-items: center; gap: 12px; padding: 12px; background: var(--bg-tertiary); border-radius: 8px; }
.hash-label { font-size: 13px; font-weight: 700; color: var(--accent-color); min-width: 70px; }
.hash-value { flex: 1; display: flex; align-items: center; gap: 8px; }
.hash-value code { flex: 1; font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 13px; color: var(--text-primary); word-break: break-all; }
.btn-copy { background: none; border: none; cursor: pointer; font-size: 16px; padding: 2px 6px; border-radius: 4px; transition: background 0.2s; }
.btn-copy:hover { background: var(--border-color); }
.action-bar { display: flex; align-items: center; gap: 12px; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border-color); }
.status-msg { font-size: 13px; color: var(--accent-color); }
</style>
